import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {ProjectAreaName, ProjectId} from "./Project";
import {Identifier, makeStringId} from "../common/Identifier";
import {Lockable} from "../common/Lockable";
import {ProjectStageId} from "./ProjectStage";
import {Nameable, UniqueNameable} from "../common/Nameable";
import {BudgetProp} from "../accounting/Budgetable";
import {Describable} from "../common/Describable";
import {Dateable, TimePeriodable} from "../common/TimePeriodable";
import {Nullable} from "../common/Nullable";
import {Money, Percent} from "../accounting/Finance";
import {Performanceable} from "../accounting/Performanceable";
import {WorkingHours} from "../timesheet/WorkingHours";
import {PlannedCostsProp} from "../accounting/PlannedCosts";
import {SoftDeletable} from "../common/SoftDeletable";
import {ProjectCustomer} from "./ProjectCustomer";
import {ProjectResourceable, ProjectResourceContainer} from "./ProjectResourceable";
import { BillSettingsProp } from "../accounting/BillSettings";
import {ProjectStatusProp} from "./ProjectStatusProp";
import {Sensitive} from "../common/Sensitive";

/**
 * Represents the module responsible for managing the stages and phases of a project.
 * This plays a critical role in controlling access and permissions within the project lifecycle.
 */
export const ProjectStagePhaseModule: Module = {
    identifier: "project_stage_phases",
    isPrivileged: ( authUser, privilege ) => isPrivileged( authUser, ProjectAreaName, ProjectStagePhaseModule.identifier, privilege )
};
/**
 * Constructs the URL route for selecting a phase within a specific project stage.
 *
 * This function dynamically generates a route string used in the application
 * to navigate and interact with the "Select Phase" section under a specific
 * project and stage. It ensures that the correct project and stage identifiers
 * are embedded in the URL format.
 *
 * @param projectsId - The unique identifier of the project.
 * @param stageId - The unique identifier of the stage within the project.
 * @return The constructed route string in the format `projects/{projectsId}/stages/{*/
/**
 * Constructs the URL route for selecting a phase within a specific project stage.
 *
 * This function dynamically generates a route string used in the application
 * to navigate and interact with the "Select Phase" section under a specific
 * project and stage. It ensures that the correct project and stage identifiers
 * are embedded in the URL format.
 *
 * @param projectsId - The unique identifier of the project.
 * @param stageId - The unique identifier of the stage within the project.
 * @return The constructed route string in the format `projects/{projectsId}/stages/{stageId}/phases/select`.
 * @throws Will not throw errors but ensure `projectsId` and `stageId` are valid integers to avoid broken routes.
 */
export const projectPhaseSelectRoute = ( projectsId: number, stageId: number ) => `projects/${projectsId}/stages/${stageId}/phases/select`;

/**
 * Represents the unique identifier for a project stage phase.
 *
 * This interface exists to model and track the identity of a specific phase within the broader
 * lifecycle of a project stage. It ensures that each phase can be uniquely referenced
 * and managed, supporting better traceability and consistency in project workflows.
 *
 * @property phaseId - A unique identifier for the phase, represented using a type-safe wrapper around a number.
 * This ensures strict typing and reduces the risk of accidental misuse.
 */
export interface ProjectStagePhaseId {
    phaseId: Identifier<number>
}

/**
 * Represents a simplified version of a project stage phase, used to track its state and essential details.
 *
 * This interface combines foundational properties for identifying and naming a project stage phase,
 * while also indicating whether the phase is locked for further modifications.
 *
 * @extends ProjectStagePhaseId Provides the unique identifier for the project stage phase.
 * @extends Nameable Includes naming capabilities for better readability and identification.
 */
export interface ProjectStagePhaseShort extends ProjectStagePhaseId, Nameable {
    isLocked: boolean
}

/**
 * A unique identifier for a specific phase within a project's lifecycle.
 *
 * This constant is used to distinguish different stages or phases of a project,
 * allowing for consistent referencing and management across various features and modules.
 * The identifier ensures that each phase can be traced programmatically without ambiguity.
 *
 * It is generated through a utility function that creates a standardized string format.
 *
 * @constant PHASE_RESOURCE_IDENTIFIER
 * @type {string}
 * @throws Will throw an error if the `makeStringId` function fails to produce a valid identifier.
 */
export const PHASE_RESOURCE_IDENTIFIER = makeStringId("ProjectStagePhase");

/**
 * Represents a specific phase of a project stage, combining business and technical attributes
 * to manage and track project progress, costs, and resources.
 *
 * This interface aggregates various properties and behaviors needed to detail, analyze,
 * and facilitate planning and execution for a specific phase of a project stage.
 * It is essential for linking operational data*/
/**
 * Represents a specific phase of a project stage, combining business and technical attributes
 * to manage and track project progress, costs, and resources.
 *
 * This interface aggregates various properties and behaviors needed to detail, analyze,
 * and facilitate planning and execution for a specific phase of a project stage.
 * It is essential for linking operational data (e.g., resources, timings, budgets)
 * to higher-level business objectives like profitability and customer satisfaction.
 *
 * This interface extends multiple other interfaces to integrate several domain-specific
 * functionalities such as resource management, cost analysis, and project lifecycle tracking.
 */
export interface ProjectStagePhase extends ProjectStageId,
    ProjectId,
    ProjectStagePhaseShort,
    Performanceable,
    PlannedCostsProp,
    BudgetProp,
    TimePeriodable< Nullable< Dateable >, Nullable< Dateable >>,
    SoftDeletable,
    ProjectResourceable,
    Describable,
    ProjectResourceContainer,
    BillSettingsProp,
    ProjectStatusProp
{
    manHoursUsed: WorkingHours
    additionalCosts: Percent

    commissions: Nullable< ProjectCustomer >

    profitMargin: number
    optionalRetailPrice: Sensitive<Money>
    phaseTemplateId: number

    additionalCostPrice?: Sensitive<Money>
    retailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalRetailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalAdditionalCosts?: Sensitive<Money>
}

/**
 * Represents an extension or customization of a project stage phase for external systems or integrations.
 *
 * This interface is designed to augment the functionality of the core `ProjectStagePhase` interface
 * by introducing the capability to interoperate with external systems or processes. It may include
 * additional properties, methods, or constraints required to handle external dependencies.
 *
 * Developers can use this interface to build features that require alignment between internal
 * project phase stages and external tools, APIs, or workflows.
 *
 * Extending this interface provides flexibility to adapt to external system requirements without
 * affecting the core implementation of `ProjectStagePhase`.
 */
export interface ProjectStagePhaseExternal extends ProjectStagePhase {
}

/**
 * Determines whether the given phase is a valid project stage phase.
 *
 * This function checks if the provided phase object matches the criteria
 * for being identified as a `ProjectStagePhase`. It ensures the required
 * resource type identifier is correct, which helps differentiate project
 * stages from other resourceable entities.
 *
 * @param phase - The resourceable object to evaluate. This object is
 * expected to be a project-related resourceable entity.
 * @return A boolean value indicating*/
/**
 * Determines whether the given phase is a valid project stage phase.
 *
 * This function checks if the provided phase object matches the criteria
 * for being identified as a `ProjectStagePhase`. It ensures the required
 * resource type identifier is correct, which helps differentiate project
 * stages from other resourceable entities.
 *
 * @param phase - The resourceable object to evaluate. This object is
 * expected to be a project-related resourceable entity.
 * @return A boolean value indicating whether the given phase is a
 * `ProjectStagePhase` (`true`) or not (`false`).
 * @throws Will not throw exceptions, but callers must ensure that
 * the `phase` parameter is properly structured and non-null.
 */
export function isProjectStagePhase(phase: ProjectResourceable ): phase is ProjectStagePhase {
    if( ! phase ) {
        return false;
    }

    return phase.resourceTypeIdentifier.rawValue === PHASE_RESOURCE_IDENTIFIER.rawValue;
}

/**
 * The type of the phase, currently there are two different types
 * @see ProjectStagePhaseTypes
 */
export interface ProjectStagePhaseType extends UniqueNameable {

}



/**
 * ProjectStagesPhaseTypes provides an easily accessible enum like structure to all types of phases
 */
export const ProjectStagePhaseTypes: {
    internal: ProjectStagePhaseType,
    external: ProjectStagePhaseType
} = {
    external: { uniqueName: 'phase_external' as const },
    internal: { uniqueName: 'phase_internal' as const }
};
